import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, Download, ExternalLink, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";

export default function AdminGallery() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("all");
  const [label, setLabel] = useState("");
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (phase !== "all") p.set("phase", phase);
    if (label) p.set("room_label", label);
    return p.toString();
  }, [phase, label]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/gallery/work-photos${params ? `?${params}` : ""}`);
      const d = unwrap(r) || {};
      setItems(d.items || []);
      setLabels(d.room_labels || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not load gallery");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const downloadZip = async () => {
    setDownloadingZip(true);
    try {
      const r = await fetch(`${API_BASE}/admin/gallery/work-photos/download-zip${params ? `?${params}` : ""}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error("failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `everclean-gallery-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
    } catch {
      toast.error("ZIP download failed");
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-gallery-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Camera className="w-6 h-6 text-[#0EA5A4]" /> Marketing Gallery
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">Every before/after photo that was approved for marketing.</p>
        </div>
        <Button
          onClick={downloadZip}
          disabled={downloadingZip || items.length === 0}
          className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white"
          data-testid="download-all-zip-btn"
        >
          {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Download all approved ({items.length})
        </Button>
      </div>

      <Card className="rounded-2xl p-4 border border-[#E5E7EB] flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {[
            { v: "all", label: "All" },
            { v: "before", label: "Before" },
            { v: "after", label: "After" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setPhase(opt.v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${phase === opt.v ? "bg-[#0EA5A4] text-white" : "bg-[#F5F5F4] text-[#4B5563] hover:bg-[#E5E7EB]"}`}
              data-testid={`filter-phase-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#9CA3AF] font-semibold uppercase">Room</span>
          <select
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 text-sm"
            data-testid="filter-room-label"
          >
            <option value="">All rooms</option>
            {labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        {(phase !== "all" || label) && (
          <button
            onClick={() => { setPhase("all"); setLabel(""); }}
            className="text-xs text-[#0EA5A4] font-semibold hover:underline"
            data-testid="clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </Card>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl p-10 border border-dashed border-[#E5E7EB] text-center">
          <Camera className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">No approved photos yet. Photos have to be manually approved from each booking's detail page.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="gallery-grid">
          {items.map((it) => (
            <GalleryTile key={it.id} item={it} onOpen={() => setLightbox(it)} onGotoBooking={() => navigate(`/admin/bookings/${it.booking_id}`)} />
          ))}
        </div>
      )}

      {lightbox && <GalleryLightbox item={lightbox} onClose={() => setLightbox(null)} onGotoBooking={() => navigate(`/admin/bookings/${lightbox.booking_id}`)} />}
    </div>
  );
}

function GalleryTile({ item, onOpen, onGotoBooking }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let active = true;
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/admin/bookings/${item.booking_id}/work-photos/${item.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setUrl(blobUrl);
      } catch { /* keep empty */ }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [item.booking_id, item.id]);

  return (
    <div className="group relative" data-testid="gallery-tile">
      <button onClick={onOpen} className="w-full aspect-square rounded-xl overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center hover:ring-2 hover:ring-[#0EA5A4] transition">
        {url ? (
          <img src={url} alt={item.room_label || "work photo"} className="w-full h-full object-cover" />
        ) : (
          <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
        )}
      </button>
      <div className="absolute top-1.5 left-1.5 flex gap-1">
        <Badge className="bg-white/95 text-[#0C1A1A] text-[10px] px-1.5 capitalize border">{item.phase}</Badge>
        {item.room_label && <Badge className="bg-white/95 text-[#0C1A1A] text-[10px] px-1.5 border">{item.room_label}</Badge>}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-[#4B5563]">
        <span className="font-mono truncate">{item.booking_number || item.booking_id.slice(-6)}</span>
        <button onClick={onGotoBooking} className="text-[#0EA5A4] hover:underline flex items-center gap-0.5" data-testid="tile-goto-booking">
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function GalleryLightbox({ item, onClose, onGotoBooking }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/admin/bookings/${item.booking_id}/work-photos/${item.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        setUrl(blobUrl);
      } catch { /* keep empty */ }
    })();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [item.booking_id, item.id]);

  const download = () => {
    if (!url) return;
    const ext = (item.content_type || "image/jpeg").split("/").pop().replace("jpeg", "jpg");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.booking_number || item.booking_id.slice(-6)}__${item.phase}__${(item.room_label || "photo").replace(/\s+/g, "-")}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4" onClick={onClose} data-testid="gallery-lightbox">
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white grid place-items-center shadow" aria-label="Close" data-testid="gallery-lightbox-close">
          <X className="w-4 h-4" />
        </button>
        <div className="bg-black rounded-xl overflow-hidden grid place-items-center min-h-[300px]">
          {url ? <img src={url} alt={item.room_label || "work photo"} className="max-h-[75vh] object-contain" /> : <Loader2 className="w-6 h-6 animate-spin text-white" />}
        </div>
        <div className="mt-3 bg-white rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold flex items-center gap-2">
              <span className="capitalize">{item.phase}</span>
              {item.room_label && <Badge variant="outline" className="text-xs">{item.room_label}</Badge>}
              <Badge className="bg-emerald-600 text-white text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>
            </div>
            <div className="text-xs text-[#9CA3AF] truncate">
              Booking {item.booking_number || item.booking_id.slice(-6)} · {item.customer_full_name || "—"} · {item.customer_city || "—"} · {item.preferred_date || "—"}
            </div>
          </div>
          <Button onClick={download} variant="outline" className="rounded-xl" data-testid="gallery-lightbox-download">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
          <Button onClick={onGotoBooking} variant="outline" className="rounded-xl" data-testid="gallery-lightbox-open-booking">
            <ExternalLink className="w-4 h-4 mr-2" /> Open booking
          </Button>
        </div>
      </div>
    </div>
  );
}
